/**
 * Indicator calculations for Strategy Builder signal evaluation.
 * All functions return arrays the same length as input, with null entries
 * during warmup periods.
 *
 * All VWAP variants use OHLC/4 (typical price) as source, per BWC convention.
 */

import {
  getEtSessionDateKey,
  getEt2DaySessionKey,
  getEtMonthKey,
  getEtYearKey,
  getEtWeekAnchorKey,
} from './chart/sessionBoundary.js'

// ═══════════════════════════════════════════════════════════════════════════
// DEFAULT SETTINGS
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_SETTINGS = {
  // Moving Averages
  sma_periods: [20, 50, 200],
  ema_periods: [21],
  sma5day_period_30min: 65,  // 65 bars on 30min = 5 trading days
  sma5day_period_daily: 5,

  // Keltner Channels (Basso default)
  keltner_period: 20,
  keltner_atr_period: 14,
  keltner_multiplier: 2.3,

  // Bollinger Bands (Basso default)
  bollinger_period: 20,
  bollinger_std_multiplier: 2.0,

  // Donchian Channels (Basso default)
  donchian_period: 20,

  // ATR
  atr_period: 14,

  // Volume
  volume_avg_period: 20,

  // Pivot Levels
  pivot_type: 'floor', // 'floor' (standard), 'woodie', 'camarilla' — only 'floor' implemented

  // VWAP source
  vwap_source: 'ohlc4',

  // AVWAP — swing detection
  avwap_swing_lookback: 5,

  // Custom periods — additional SMAs/EMAs to pre-compute and expose under
  // dynamic keys (e.g., sma30, ema9). Populated by the block builder when
  // a user picks a non-standard period.
  customSMAs: [],
  customEMAs: [],
}

// ═══════════════════════════════════════════════════════════════════════════
// CORE HELPERS
// ═══════════════════════════════════════════════════════════════════════════

export function calcSMA(closes, period) {
  const result = new Array(closes.length).fill(null)
  if (period <= 0 || closes.length < period) return result
  let sum = 0
  for (let i = 0; i < closes.length; i++) {
    sum += closes[i]
    if (i >= period) sum -= closes[i - period]
    if (i >= period - 1) result[i] = sum / period
  }
  return result
}

export function calcEMA(closes, period) {
  const result = new Array(closes.length).fill(null)
  if (period <= 0 || closes.length === 0) return result
  if (closes.length < period) return result
  const k = 2 / (period + 1)
  let seed = 0
  for (let i = 0; i < period; i++) seed += closes[i]
  seed /= period
  result[period - 1] = seed
  for (let i = period; i < closes.length; i++) {
    result[i] = closes[i] * k + result[i - 1] * (1 - k)
  }
  return result
}

export function calcATR(candles, period) {
  const result = new Array(candles.length).fill(null)
  if (period <= 0 || candles.length === 0) return result
  const trs = candles.map((c, i) => {
    if (i === 0) return c.high - c.low
    const prev = candles[i - 1]
    return Math.max(
      c.high - c.low,
      Math.abs(c.high - prev.close),
      Math.abs(c.low - prev.close),
    )
  })
  let sum = 0
  for (let i = 0; i < candles.length; i++) {
    sum += trs[i]
    if (i >= period) sum -= trs[i - period]
    if (i >= period - 1) result[i] = sum / period
  }
  return result
}

export function calcVolumeAvg(volumes, period) {
  return calcSMA(volumes, period)
}

export function calcTypicalPrice(candles) {
  return candles.map(c => (c.open + c.high + c.low + c.close) / 4)
}

// ═══════════════════════════════════════════════════════════════════════════
// CHANNELS
// ═══════════════════════════════════════════════════════════════════════════

export function calcKeltnerChannels(candles, emaPeriod, atrPeriod, multiplier) {
  const closes = candles.map(c => c.close)
  const middle = calcEMA(closes, emaPeriod)
  const atr = calcATR(candles, atrPeriod)
  const upper = middle.map((m, i) => (m !== null && atr[i] !== null) ? m + atr[i] * multiplier : null)
  const lower = middle.map((m, i) => (m !== null && atr[i] !== null) ? m - atr[i] * multiplier : null)
  return { upper, middle, lower }
}

export function calcBollingerBands(closes, period, multiplier) {
  const middle = calcSMA(closes, period)
  const upper = new Array(closes.length).fill(null)
  const lower = new Array(closes.length).fill(null)

  for (let i = period - 1; i < closes.length; i++) {
    if (middle[i] === null) continue
    let sumSq = 0
    for (let j = i - period + 1; j <= i; j++) {
      const diff = closes[j] - middle[i]
      sumSq += diff * diff
    }
    const stdDev = Math.sqrt(sumSq / period)
    upper[i] = middle[i] + stdDev * multiplier
    lower[i] = middle[i] - stdDev * multiplier
  }
  return { upper, middle, lower }
}

export function calcDonchianChannels(candles, period) {
  const upper = new Array(candles.length).fill(null)
  const lower = new Array(candles.length).fill(null)
  const middle = new Array(candles.length).fill(null)

  for (let i = period - 1; i < candles.length; i++) {
    let hi = -Infinity, lo = Infinity
    for (let j = i - period + 1; j <= i; j++) {
      if (candles[j].high > hi) hi = candles[j].high
      if (candles[j].low < lo) lo = candles[j].low
    }
    upper[i] = hi
    lower[i] = lo
    middle[i] = (hi + lo) / 2
  }
  return { upper, middle, lower }
}

// ═══════════════════════════════════════════════════════════════════════════
// PIVOT LEVELS (floor/standard)
// ═══════════════════════════════════════════════════════════════════════════

// Legacy UTC-`time` day-bucket key. Preserved as the fallback when a candle
// is missing `rawEpochMs` (strategy-snapshot path).
function legacyDayKey(timeEpochSeconds) {
  const date = new Date(timeEpochSeconds * 1000)
  return `${date.getUTCFullYear()}-${date.getUTCMonth()}-${date.getUTCDate()}`
}

// CE-04g-UI-vwap-anchor: day-bucket via ET semantics when `rawEpochMs` is
// present; otherwise fall back to legacy UTC keying for byte-for-byte
// compatibility with callers that don't thread rawEpochMs (StrategyChart).
function dayKey(candle) {
  if (candle && candle.rawEpochMs != null) {
    return getEtSessionDateKey(candle.rawEpochMs)
  }
  return legacyDayKey(candle.time)
}

export function calcPivotLevels(candles, barSize) {
  const result = {
    pivot: new Array(candles.length).fill(null),
    r1: new Array(candles.length).fill(null),
    r2: new Array(candles.length).fill(null),
    r3: new Array(candles.length).fill(null),
    s1: new Array(candles.length).fill(null),
    s2: new Array(candles.length).fill(null),
    s3: new Array(candles.length).fill(null),
  }

  if (candles.length === 0) return result

  if (barSize === '1 day' || barSize === '1 week') {
    // Daily/weekly: pivot derives from the immediately prior bar
    // TradingView Pivot Points Standard (Classic): https://www.tradingview.com/support/solutions/43000521824-pivot-points-standard/
    for (let i = 1; i < candles.length; i++) {
      const prev = candles[i - 1]
      const pp = (prev.high + prev.low + prev.close) / 3
      result.pivot[i] = pp
      result.r1[i] = 2 * pp - prev.low
      result.s1[i] = 2 * pp - prev.high
      result.r2[i] = pp + (prev.high - prev.low)
      result.s2[i] = pp - (prev.high - prev.low)
      result.r3[i] = pp + 2 * (prev.high - prev.low)
      result.s3[i] = pp - 2 * (prev.high - prev.low)
    }
    return result
  }

  // Intraday: group bars into days via the ET date key (when rawEpochMs is
  // present) or the legacy UTC key (fallback).
  const keys = candles.map(c => dayKey(c))
  // Compute each day's HLC
  const dayStats = new Map() // key -> { high, low, close, lastIndex }
  for (let i = 0; i < candles.length; i++) {
    const k = keys[i]
    const s = dayStats.get(k)
    if (!s) {
      dayStats.set(k, { high: candles[i].high, low: candles[i].low, close: candles[i].close, lastIndex: i })
    } else {
      if (candles[i].high > s.high) s.high = candles[i].high
      if (candles[i].low < s.low) s.low = candles[i].low
      s.close = candles[i].close
      s.lastIndex = i
    }
  }

  // Build an ordered list of day keys as they first appear
  const orderedKeys = []
  const seen = new Set()
  for (const k of keys) {
    if (!seen.has(k)) { seen.add(k); orderedKeys.push(k) }
  }
  const keyIndex = new Map(orderedKeys.map((k, idx) => [k, idx]))

  // For each bar, look up the PRIOR day's stats (if any) and compute pivots
  for (let i = 0; i < candles.length; i++) {
    const idx = keyIndex.get(keys[i])
    if (idx === 0) continue // first day has no prior
    const prevKey = orderedKeys[idx - 1]
    const prev = dayStats.get(prevKey)
    if (!prev) continue
    const pp = (prev.high + prev.low + prev.close) / 3
    result.pivot[i] = pp
    result.r1[i] = 2 * pp - prev.low
    result.s1[i] = 2 * pp - prev.high
    result.r2[i] = pp + (prev.high - prev.low)
    result.s2[i] = pp - (prev.high - prev.low)
    result.r3[i] = pp + 2 * (prev.high - prev.low)
    result.s3[i] = pp - 2 * (prev.high - prev.low)
  }

  return result
}

// ═══════════════════════════════════════════════════════════════════════════
// SWING DETECTION + ANCHORED VWAP
// ═══════════════════════════════════════════════════════════════════════════

export function detectSwings(candles, lookback) {
  const swingHighs = []
  const swingLows = []

  for (let i = lookback; i < candles.length - lookback; i++) {
    let isHigh = true, isLow = true
    for (let j = 1; j <= lookback; j++) {
      if (candles[i].high <= candles[i - j].high || candles[i].high <= candles[i + j].high) isHigh = false
      if (candles[i].low >= candles[i - j].low || candles[i].low >= candles[i + j].low) isLow = false
      if (!isHigh && !isLow) break
    }
    if (isHigh) swingHighs.push({ index: i, price: candles[i].high })
    if (isLow) swingLows.push({ index: i, price: candles[i].low })
  }
  return { swingHighs, swingLows }
}

// Source = OHLC/4
export function calcAVWAP(candles, startIndex) {
  const result = new Array(candles.length).fill(null)
  if (startIndex < 0 || startIndex >= candles.length) return result
  let cumTPV = 0
  let cumVol = 0
  for (let i = startIndex; i < candles.length; i++) {
    const c = candles[i]
    const tp = (c.open + c.high + c.low + c.close) / 4
    const vol = Number(c.volume) || 0
    cumTPV += tp * vol
    cumVol += vol
    result[i] = cumVol > 0 ? cumTPV / cumVol : null
  }
  return result
}

export function calcSwingAVWAPs(candles, swingLookback) {
  const { swingHighs, swingLows } = detectSwings(candles, swingLookback)

  const fromHighs = swingHighs.map(sh => ({
    anchorIndex: sh.index,
    anchorPrice: sh.price,
    values: calcAVWAP(candles, sh.index),
  }))

  const fromLows = swingLows.map(sl => ({
    anchorIndex: sl.index,
    anchorPrice: sl.price,
    values: calcAVWAP(candles, sl.index),
  }))

  // Expose only the 3 most recent on each side — more than that is noise
  return {
    fromHighs: fromHighs.slice(-3),
    fromLows: fromLows.slice(-3),
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// SESSION-RESETTING VWAPs (OHLC/4 source)
// ═══════════════════════════════════════════════════════════════════════════

// Legacy UTC-`time` session-key dispatch — preserved as the fallback when a
// candle is missing `rawEpochMs`. Byte-for-byte compatible with pre-
// CE-04g-UI-vwap-anchor callers (StrategyChart.jsx snapshot path).
function legacyGetSessionKey(candle, resetType) {
  const date = new Date(candle.time * 1000)
  const y = date.getUTCFullYear()
  const m = date.getUTCMonth()
  const d = date.getUTCDate()
  const dow = date.getUTCDay() // 0=Sun

  switch (resetType) {
    case '1day':
      return `${y}-${m}-${d}`
    case '2day': {
      const startOfYear = Date.UTC(y, 0, 0)
      const doy = Math.floor((Date.UTC(y, m, d) - startOfYear) / 86400000)
      return `${y}-${Math.floor(doy / 2)}`
    }
    case 'wtd': {
      const offset = (dow + 6) % 7
      const monday = new Date(Date.UTC(y, m, d))
      monday.setUTCDate(monday.getUTCDate() - offset)
      return `${monday.getUTCFullYear()}-${monday.getUTCMonth()}-${monday.getUTCDate()}`
    }
    case 'mtd':
      return `${y}-${m}`
    case 'ytd':
      return `${y}`
    default:
      return `${y}-${m}-${d}`
  }
}

// CE-04g-UI-vwap-anchor: ET-aware session-key dispatch. When the candle
// carries `rawEpochMs`, route to the ET helpers in sessionBoundary.js so
// every reset boundary matches the real ET calendar. Falls back to the
// legacy UTC keying for any candle missing `rawEpochMs`.
function getSessionKey(candle, resetType, tradingCalendar) {
  if (candle == null || candle.rawEpochMs == null) {
    return legacyGetSessionKey(candle, resetType)
  }
  const rawEpochMs = candle.rawEpochMs
  switch (resetType) {
    case '1day':
      return getEtSessionDateKey(rawEpochMs)
    case '2day':
      return getEt2DaySessionKey(rawEpochMs)
    case 'wtd':
      return getEtWeekAnchorKey(rawEpochMs, tradingCalendar)
    case 'mtd':
      return getEtMonthKey(rawEpochMs)
    case 'ytd':
      return getEtYearKey(rawEpochMs)
    default:
      return getEtSessionDateKey(rawEpochMs)
  }
}

export function calcSessionVWAP(candles, resetType, tradingCalendar) {
  const result = new Array(candles.length).fill(null)
  let cumTPV = 0
  let cumVol = 0
  let currentSession = null

  for (let i = 0; i < candles.length; i++) {
    const session = getSessionKey(candles[i], resetType, tradingCalendar)
    if (session !== currentSession) {
      cumTPV = 0
      cumVol = 0
      currentSession = session
    }
    const c = candles[i]
    const tp = (c.open + c.high + c.low + c.close) / 4
    const vol = Number(c.volume) || 0
    cumTPV += tp * vol
    cumVol += vol
    result[i] = cumVol > 0 ? cumTPV / cumVol : null
  }
  return result
}

// ═══════════════════════════════════════════════════════════════════════════
// MASTER: computeAllIndicators
// ═══════════════════════════════════════════════════════════════════════════

export function computeAllIndicators(candles, barSize = '1 day', settings = {}) {
  const cfg = { ...DEFAULT_SETTINGS, ...settings }
  const closes = candles.map(c => Number(c.close) || 0)
  const volumes = candles.map(c => Number(c.volume) || 0)

  // All MAs in Erevis are computed from 30-min bars to represent daily-timeframe
  // values. When source is 30-min, multiply day-denominated periods by 13
  // (bars per RTH trading day). When source is daily, use periods as-is.
  const is30 = barSize === '30 mins'
  const barsPerDay = 13
  const scale = (days) => (is30 ? days * barsPerDay : days)

  const sma5dayPeriod = is30 ? cfg.sma5day_period_30min : cfg.sma5day_period_daily
  const sma20Period = scale(cfg.sma_periods[0] || 20)
  const sma50Period = scale(cfg.sma_periods[1] || 50)
  const sma200Period = scale(cfg.sma_periods[2] || 200)
  const ema21Period = scale(cfg.ema_periods[0] || 21)

  // Core MAs
  const sma20 = calcSMA(closes, sma20Period)
  const sma50 = calcSMA(closes, sma50Period)
  const sma200 = calcSMA(closes, sma200Period)
  const ema21 = calcEMA(closes, ema21Period)
  const sma5day = calcSMA(closes, sma5dayPeriod)

  // ATR
  const atr14 = calcATR(candles, cfg.atr_period)

  // Volume
  const volume20avg = calcSMA(volumes, cfg.volume_avg_period)
  const rvol = volumes.map((v, i) => (volume20avg[i] && volume20avg[i] > 0 ? v / volume20avg[i] : null))

  // Channels
  const keltner = calcKeltnerChannels(candles, cfg.keltner_period, cfg.keltner_atr_period, cfg.keltner_multiplier)
  const bollinger = calcBollingerBands(closes, cfg.bollinger_period, cfg.bollinger_std_multiplier)
  const donchian = calcDonchianChannels(candles, cfg.donchian_period)

  // Pivots
  const pivots = calcPivotLevels(candles, barSize)

  // Session VWAPs (OHLC/4 source)
  const vwap1day = calcSessionVWAP(candles, '1day')
  const vwap2day = calcSessionVWAP(candles, '2day')
  const vwapWtd = calcSessionVWAP(candles, 'wtd')
  const vwapMtd = calcSessionVWAP(candles, 'mtd')
  const vwapYtd = calcSessionVWAP(candles, 'ytd')

  // AVWAPs from swing highs/lows
  const swingAvwaps = calcSwingAVWAPs(candles, cfg.avwap_swing_lookback)

  // Custom-period SMAs/EMAs (dynamic keys like sma30, ema9). The user-supplied
  // period is expressed in DAYS — scaled to 30-min bars when the source is 30-min.
  const customOut = {}
  const STANDARD_SMAS = new Set([20, 50, 200])
  const STANDARD_EMAS = new Set([21])
  for (const p of (cfg.customSMAs || [])) {
    const period = Number(p)
    if (!isFinite(period) || period <= 0 || STANDARD_SMAS.has(period)) continue
    customOut[`sma${period}`] = calcSMA(closes, scale(period))
  }
  for (const p of (cfg.customEMAs || [])) {
    const period = Number(p)
    if (!isFinite(period) || period <= 0 || STANDARD_EMAS.has(period)) continue
    customOut[`ema${period}`] = calcEMA(closes, scale(period))
  }

  return {
    candles,

    // Moving Averages
    sma20, sma50, sma200, ema21, sma5day,

    // ATR
    atr14,

    // Volume
    volume20avg, rvol,

    // Custom-period SMAs/EMAs (spread into top-level keys)
    ...customOut,

    // Keltner Channels
    keltnerUpper: keltner.upper,
    keltnerMiddle: keltner.middle,
    keltnerLower: keltner.lower,

    // Bollinger Bands
    bollingerUpper: bollinger.upper,
    bollingerMiddle: bollinger.middle,
    bollingerLower: bollinger.lower,

    // Donchian Channels
    donchianUpper: donchian.upper,
    donchianMiddle: donchian.middle,
    donchianLower: donchian.lower,

    // Pivot Levels (floor/standard)
    pivotPP: pivots.pivot,
    pivotR1: pivots.r1,
    pivotR2: pivots.r2,
    pivotR3: pivots.r3,
    pivotS1: pivots.s1,
    pivotS2: pivots.s2,
    pivotS3: pivots.s3,

    // Session VWAPs (OHLC/4)
    vwap1day,
    vwap2day,
    vwapWtd,
    vwapMtd,
    vwapYtd,

    // Anchored VWAPs from swing highs/lows
    avwapFromHighs: swingAvwaps.fromHighs,
    avwapFromLows: swingAvwaps.fromLows,
  }
}
